/**
 * DosingEventFilter - Clickable chip tiles to filter dosing events by chemical name
 *
 * Follows the same UX pattern as TaskCompletionsList: a primary row of chemical-name
 * chips and an optional second row of source-type chips when a chemical has mixed sources.
 *
 * This is a controlled presentational component — filter state is owned by the parent.
 */
import { useMemo } from 'react';
import { Group, Chip, Text } from '@mantine/core';
import type { DosingEvent } from '@/types/dosing';

export interface DosingEventFilterProps {
  events: DosingEvent[];
  selectedChemical: string | null;
  selectedSource: string | null;
  onChemicalChange: (chemical: string | null) => void;
  onSourceChange: (source: string | null) => void;
  filteredCount: number;
}

/** Map raw source values to user-friendly labels */
function sourceLabel(source: string): string {
  switch (source) {
    case 'manual':
      return 'Manual';
    case 'task':
      return 'Task';
    case 'schedule':
      return 'Schedule';
    case 'kamoer_kh':
      return 'KH Carer';
    case 'kamoer_stp':
      return 'STP Connect';
    case 'kamoer_p4':
      return 'P4 Pro';
    case 'dosetronic':
      return 'Dosetronic';
    default:
      return source;
  }
}

/** Normalise undefined / missing source to 'dosetronic' for backward compat */
export function normaliseSource(event: DosingEvent): string {
  if (event.source) return event.source;
  return event.pump_number === 0 ? 'manual' : 'dosetronic';
}

export function DosingEventFilter({
  events,
  selectedChemical,
  selectedSource,
  onChemicalChange,
  onSourceChange,
  filteredCount,
}: DosingEventFilterProps) {
  // Derive unique chemicals with counts, sorted alphabetically
  const chemicalCounts = useMemo(() => {
    const counts = new Map<string, number>();
    events.forEach((e) => {
      const name = e.chemical_name || 'Unknown';
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b));
  }, [events]);

  // Derive unique source types for the selected chemical
  const availableSources = useMemo(() => {
    if (!selectedChemical) return [];
    const sources = new Set<string>();
    events
      .filter((e) => (e.chemical_name || 'Unknown') === selectedChemical)
      .forEach((e) => sources.add(normaliseSource(e)));
    return Array.from(sources).sort();
  }, [events, selectedChemical]);

  // Don't render filter bar when there's nothing useful to filter
  if (chemicalCounts.length <= 1) {
    return null;
  }

  return (
    <>
      {/* Chemical filter chips */}
      <Group gap="xs" wrap="wrap" data-testid="chemical-filter">
        <Chip
          checked={selectedChemical === null}
          onChange={() => onChemicalChange(null)}
          size="xs"
          variant="light"
        >
          All
        </Chip>
        {chemicalCounts.map(([chemical, count]) => (
          <Chip
            key={chemical}
            checked={selectedChemical === chemical}
            onChange={() =>
              onChemicalChange(
                selectedChemical === chemical ? null : chemical,
              )
            }
            size="xs"
            variant="light"
            color="teal"
          >
            {chemical} ({count})
          </Chip>
        ))}
      </Group>

      {/* Source filter chips (second level) — only when selected chemical has multiple sources */}
      {selectedChemical && availableSources.length > 1 && (
        <Group gap="xs" wrap="wrap" data-testid="source-filter">
          <Chip
            checked={selectedSource === null}
            onChange={() => onSourceChange(null)}
            size="xs"
            variant="outline"
          >
            All {selectedChemical}
          </Chip>
          {availableSources.map((source) => (
            <Chip
              key={source}
              checked={selectedSource === source}
              onChange={() =>
                onSourceChange(
                  selectedSource === source ? null : source,
                )
              }
              size="xs"
              variant="outline"
            >
              {sourceLabel(source)}
            </Chip>
          ))}
        </Group>
      )}

      {/* Filtered count indicator */}
      {filteredCount !== events.length && (
        <Text size="xs" c="dimmed">
          Showing {filteredCount} of {events.length} events
        </Text>
      )}
    </>
  );
}
